import * as React from 'react';
import { useState, SetStateAction, useEffect } from 'react';
import { useRebook } from '../RebookContext';
import * as TFF from '@tff/types/TFF';
import { FlightDetails as NDCFlightDetails } from '@tff/types/ndc';
import Header from '../RebookFlightCard/Header';
import Title from '../RebookFlightCard/Title';
import SearchAlternatives from '../RebookFlightCard/SearchAlternatives';
import ChangeFlightCard from '../RebookFlightCard/ChangeFlightCard';
import { TypeOfStatus } from '../../../hooks/use-amend-booking';
import { AmendBookingRequest } from '@tff/types/TFF/amend-booking-service';
import TFFAlert from '../../../styled-components/TFFAlert';
import TFFTypography from '../../../styled-components/TFFTypography';
import TFFDialog from '../../../styled-components/TFFDialog';
import { useIntl } from 'react-intl';
import { RecordLocator } from '@tff/types';

type CabinProduct = 'ECONOMY' | 'BUSINESS';

export type SelectedAmendBookings = {
  combiHashCodes: string[];
  journeyONDs: string[];
  offerPrices: string[];
};

interface props {
  orderDetails: TFF.OrderDetails;
  isRoundTrip: boolean;
  journeys: TFF.Journey[];
  airports: TFF.Airport[];
  recordLocator: string;
  selectedAmendBookings: SelectedAmendBookings;
  setSelectedAmendBookings: (value: SetStateAction<SelectedAmendBookings>) => void;
  tfmPnr: string | undefined;
  rebookCloser: (fn: SetStateAction<boolean>) => void;
  flightDetails: TFF.FlightDetails;
  midocoOrderNo?: number;
  cancelHandler: () => void;
  retrieveDb?: TFF.Retrieve;
  searchFinished: () => void;
  setAmendBookingRequest: (request: AmendBookingRequest) => void;
  amendResponse?: TFF.AmendBookingResponse;
  amendStatus?: TypeOfStatus;
  amendError?: Error | undefined;
}
const SearchAlternativeFlights: React.FC<props> = ({
  journeys,
  orderDetails,
  airports,
  recordLocator,
  rebookCloser,
  flightDetails,
  midocoOrderNo,
  cancelHandler,
  retrieveDb,
  searchFinished,
  setAmendBookingRequest,
  amendResponse,
  amendStatus,
  amendError,
  isRoundTrip,
}) => {
  const { outboundFlights, returnFlights, enableSearchAlternatives, toggleSearchAlternatives, rebookSearchState } =
    useRebook();

  const [searchResults, setSearchResults] = useState<boolean>(false);
  const [showWarning, setShowWarning] = useState<boolean>(false);
  const [searchAlternativeFlights, setSearchAlternativeFlights] = useState<boolean>(false);
  const [isSearchingForLowerClass, setIsSearchingForLowerClass] = useState<boolean>(false);
  const [isLowerClass, setIsLowerClass] = useState<boolean>(false);
  const [outboundCabinClass, setOutboundCabinClass] = useState<string>('');
  const [inboundCabinClass, setInboundCabinClass] = useState<string>('');
  const intl = useIntl();

  const handleRequest = async (dialog?: boolean) => {
    if (isLowerClass) {
      setIsSearchingForLowerClass(true);
    }
    const firstJourney = orderDetails.Journeys[0];
    const outboundSearchFields = rebookSearchState['Outbound'][0];
    const [outboundDeparture, outboundOrigin, outboundDestination] = outboundSearchFields;

    let flightDetailsRequest: NDCFlightDetails[] = [
      {
        Origin: outboundOrigin,
        Destination: outboundDestination,
        DepDate: outboundDeparture,
        CabinProduct: outboundSearchFields[outboundSearchFields.length - 1].toUpperCase() as CabinProduct,
      },
    ];

    let journeyOndsRequest = [firstJourney.Ond!];

    if (isRoundTrip) {
      const lastJourney = orderDetails.Journeys[orderDetails.Journeys.length - 1];
      const inboundSearchFields = rebookSearchState['Return'][0] ?? '';
      const [inboundDeparture, inboundOrigin, inboundDestination] = inboundSearchFields;

      flightDetailsRequest.push({
        Origin: inboundOrigin,
        Destination: inboundDestination,
        DepDate: inboundDeparture,
        CabinProduct: inboundSearchFields[inboundSearchFields.length - 1].toUpperCase() as CabinProduct,
      });

      journeyOndsRequest.push(lastJourney.Ond!);
    }

    const searchRequest: AmendBookingRequest = {
      action: 'search-alternatives',
      // @ts-ignore
      bookingSource: `TUI-NDC-${retrieveDb?.bookingSource}`,
      recordLocator: recordLocator as RecordLocator,
      tfmPnr: retrieveDb?.tfmPnr!,
      midocoOrderNo: midocoOrderNo,
      flightDetails: flightDetailsRequest,
      journeyONDs: journeyOndsRequest,
    };

    setAmendBookingRequest(searchRequest);
    setSearchAlternativeFlights(true);
  };

  useEffect(() => {
    if (searchAlternativeFlights && amendStatus !== 'IN_VALIDATING' && (amendResponse || amendStatus === 'FAILURE')) {
      if (amendStatus === 'FAILURE') {
        // We should know what to do if the request presents a failure
        setShowWarning(true);
      } else {
        setShowWarning(false);
      }
    }
  }, [amendStatus]);

  useEffect(() => {
    if (outboundFlights.length == 0 && returnFlights.length == 0 && enableSearchAlternatives) {
      setSearchResults(true);
    } else {
      setSearchResults(false);
    }
  }, [outboundFlights, returnFlights]);

  useEffect(() => {
    setInboundCabinClass(outboundCabinClass);
  }, [outboundCabinClass]);

  useEffect(() => {
    setOutboundCabinClass(inboundCabinClass);
  }, [inboundCabinClass]);

  return (
    <>
      {journeys.map((j: TFF.Journey, index) => {
        const isFirstJourney = index === 0;
        return (
          <React.Fragment key={j.JourneyIdentifier}>
            {isFirstJourney && (
              <>
                {showWarning && <TFFAlert variant="warning" text={`${amendError}`} extraStyles={{ width: '100%' }} />}
                <Title orderDetails={orderDetails} recordLocator={recordLocator} />
              </>
            )}
            <Header isFirstJourney={isFirstJourney} />
            <ChangeFlightCard
              journey={j}
              airports={airports}
              orderDetails={orderDetails}
              retrieveDb={retrieveDb}
              setEnableSearchAlternatives={toggleSearchAlternatives}
              setLowerClassForNDC={setIsLowerClass}
              setSelectedCabinClassForNDC={index === 0 ? setOutboundCabinClass : setInboundCabinClass}
              selectedCabinClassForNDC={index === 0 ? outboundCabinClass : inboundCabinClass}
            />
          </React.Fragment>
        );
      })}
      <SearchAlternatives
        handleRequest={handleRequest}
        rebookCloser={rebookCloser}
        dataChanged
        cancelHandler={cancelHandler}
      />
      <TFFDialog
        dialogId="validation"
        header={<TFFTypography text="Amend notification" fontWeight="bold" variant="lead" size="large" />}
        isOpen={isSearchingForLowerClass}
        children={<TFFAlert variant="warning" text={`${intl.formatMessage({ id: 'rebook.CabinWarning' })}`} />}
        closeButton
        onCancel={() => {
          setIsSearchingForLowerClass(false);
        }}
        closeButtonText="Close"
        extraStyle={{ zIndex: 10000 }}
      />
    </>
  );
};

export default SearchAlternativeFlights;
